import { entity, legalPaths } from '../entity';
import {
  P, H3, Ul, Callout, Ext, Tok,
} from '../blocks';
import type { LegalDoc } from '../types';

// Biometric & Consumer Health Data Notice (English).
//
// This document exists because two statutes require it, and it must stay
// separate from the main privacy policy:
//
//  - Illinois BIPA (740 ILCS 14/15(a)) requires a *publicly available written
//    policy* establishing a retention schedule and destruction guidelines. A
//    paragraph inside another document does not satisfy it.
//  - Washington MHMDA requires the consumer-health-data notice to be a separate
//    and distinct homepage link that may not be combined with other notices.
//    MHMDA's definition of consumer health data enumerates biometric data, and
//    its definition of biometric data covers face imagery from which an
//    identifier template can be extracted — which is what our avatar/verification
//    face comparison does.
//
// The retention schedule below is a binding commitment. Section "retention"
// promises destruction 3 years after last interaction; that outer bound needs a
// dormancy sweep to be true for accounts that go inactive without being deleted.

export const biometricEn: LegalDoc = {
  lang: 'en',
  title: 'Biometric & Consumer Health Data Notice',
  updatedLabel: `Last updated ${entity.effectiveDate}`,
  contentsLabel: 'Contents',
  altHref: legalPaths.biometricEs,
  altLabel: 'Ver en español',
  sections: [
    {
      id: 'why',
      heading: '1. Why this is a separate document',
      body: (
        <>
          <P>
            Tribys offers optional identity verification. If you use it, we
            process an image of your face — which is biometric information, and
            which several US states regulate specifically. Illinois requires us to
            publish a written policy setting out how long we keep biometric
            information and how we destroy it. Washington requires the notice
            covering this kind of data to be published separately from our main
            privacy policy. So this is that document.
          </P>
          <P>
            Everything else about how Tribys handles your information is in the{' '}
            <a href={legalPaths.privacy}>Privacy Policy</a>. This notice adds to
            it and, for biometric and consumer health data, takes precedence.
          </P>
          <Callout>
            <strong>Identity verification is entirely optional.</strong> You can
            use Tribys without it — there is a Skip button on the verification
            screen. If you skip it, nothing in this notice applies to you, because
            we never collect any biometric information about you.
          </Callout>
        </>
      ),
    },
    {
      id: 'what',
      heading: '2. What we collect, and what we do not',
      body: (
        <>
          <P>If you choose to verify your identity, the process collects:</P>
          <Ul
            items={[
              'An image of your government-issued identity document.',
              'A short video selfie, used to confirm a live person is present (a "liveness" check).',
              'A single still reference image of your face, extracted from that check.',
              'The name, date of birth and gender printed on your document.',
              'What kind of document you used — for example an identity card or a passport.',
            ]}
          />
          <P>
            <strong>What Tribys itself keeps</strong> is deliberately narrow: the
            verified name, date of birth and gender, the kind of document you used,
            one still reference image of your face, and the date you consented.
            That is all.
          </P>
          <P>
            <strong>What Tribys never stores:</strong> your identity document
            image, <strong>your document number</strong>, any personal or national
            identification number on it, the verification video, or any age
            estimate produced during the check. These are handled by our
            verification processor and are not retained in our systems.
          </P>
          <P>
            Separately, when you upload a profile photo <em>and</em> you are
            verified, we compare the face in that photo against your stored
            reference image, and we keep the numeric similarity score of that
            comparison. We do not build or store a reusable faceprint template, and
            we do not enrol your face in any searchable face database — each
            comparison is a one-off between two images.
          </P>
        </>
      ),
    },
    {
      id: 'why-we-use-it',
      heading: '3. Why we use it',
      body: (
        <>
          <P>
            Tribys puts strangers in a room together. Biometric verification is
            used for exactly two purposes, both about that:
          </P>
          <Ul
            items={[
              <><strong>Confirming you are a real person, once.</strong> This is what earns the verified badge, lets you create verified-only groups, and lets you create a referral code.</>,
              <><strong>Stopping someone from putting a different person&apos;s face on a verified profile.</strong> This is why later profile photos are compared against your reference image. Without it, the badge would be worthless — anyone could verify with their own ID and then swap in someone else&apos;s photo.</>,
            ]}
          />
          <P>
            We do not use biometric information for anything else. Not for
            advertising, not for analytics, not for training models, not for
            tracking you across the app, and not to identify you in other
            members&apos; photos.
          </P>
        </>
      ),
    },
    {
      id: 'consent',
      heading: '4. Consent',
      body: (
        <>
          <P>
            We ask for your written consent before any biometric information is
            captured. On the verification screen you must actively tick a box
            confirming that you consent to the collection and processing of your
            biometric data and your government-issued ID. That tick is your
            written release, we record the date and time you gave it, and you can
            see that date in your data export.
          </P>
          <P>
            You may refuse. Verification is optional, the screen has a Skip
            button, and refusing does not restrict your use of Tribys beyond the
            three verified-only features listed above.
          </P>
          <P>
            <strong>You may withdraw your consent at any time</strong> by emailing{' '}
            <Tok value={entity.privacyEmail} />, or by deleting your account. When
            you withdraw consent we destroy your reference image and verification
            record, instruct our processor to delete its copy, and remove your
            verified status. Withdrawing consent does not undo comparisons already
            performed.
          </P>
        </>
      ),
    },
    {
      id: 'who',
      heading: '5. Who else is involved',
      body: (
        <>
          <P>
            Two providers process biometric information on our behalf. Both act
            only on our instructions and may not use your information for their
            own purposes.
          </P>
          <Ul
            items={[
              <><strong>Didit</strong> performs the identity check itself — reading your document and running the liveness check. Their terms are at{' '}<Ext href="https://didit.me/terms/privacy-policy/">didit.me/terms/privacy-policy</Ext>.</>,
              <><strong>Amazon Web Services</strong> performs the face comparison between a new profile photo and your reference image, using Amazon Rekognition, and stores your reference image.</>,
            ]}
          />
          <P>
            We do not disclose, redisclose or otherwise disseminate biometric
            information to anyone else, unless you consent, or a subpoena, warrant
            or other legal requirement compels us.
          </P>
        </>
      ),
    },
    {
      id: 'retention',
      heading: '6. Retention and destruction schedule',
      body: (
        <>
          <P>
            This section is our retention schedule and destruction guidelines, as
            Illinois law requires us to publish.
          </P>
          <H3>Identity document and verification video</H3>
          <P>
            Held only by our verification processor, never by us, and{' '}
            <strong>deleted after 6 months</strong>.
          </P>
          <H3>Your facial reference image, and comparison scores</H3>
          <P>We destroy these at the earliest of:</P>
          <Ul
            items={[
              'when you delete your account — the image is deleted from our storage as part of the deletion process described in the Privacy Policy;',
              'when you withdraw your biometric consent;',
              'when the purpose is satisfied — that is, when you are no longer a verified member;',
              'three years after your last interaction with Tribys.',
            ]}
          />
          <P>
            The three-year outer limit applies no matter what, including to an
            account that simply goes dormant and is never deleted. For Texas
            residents we additionally destroy biometric identifiers within one year
            of the purpose for collecting them expiring.
          </P>
          <H3>Verified name, date of birth and gender</H3>
          <P>
            These are not biometric information, but they come from your document,
            so we treat them together: kept while your account is verified, erased
            when you delete your account or withdraw consent.
          </P>
          <H3>How we destroy it</H3>
          <P>
            Destruction means permanent deletion of the stored object and its
            database record, not archiving and not anonymising. Deletion runs
            automatically, and we also instruct our processor to delete its copy by
            API. We do not keep backup copies of biometric information outside the
            normal short-term backup cycle of the storage systems themselves.
          </P>
        </>
      ),
    },
    {
      id: 'no-sale',
      heading: '7. We never sell or profit from biometric information',
      body: (
        <Callout>
          <strong>
            Tribys does not and will not sell, lease, trade, or otherwise profit
            from your biometric identifiers or biometric information, or from your
            consumer health data.
          </strong>{' '}
          There is no circumstance in which we would. We also do not use it for
          advertising, and we run no advertising or analytics services at all.
        </Callout>
      ),
    },
    {
      id: 'security',
      heading: '8. How we protect it',
      body: (
        <P>
          We protect biometric information using at least the standard of care we
          apply to other confidential and sensitive information, and in the same or
          a more protective manner. Your reference image is stored in private cloud
          storage that is encrypted at rest, is not publicly listable, and is not
          served from our public content network. Only the backend service that
          performs the comparison can read it, using short-lived federated
          credentials rather than stored access keys. All transfers use TLS 1.2 or
          better. Access is limited to the services that need it.
        </P>
      ),
    },
    {
      id: 'health',
      heading: '9. Consumer health data',
      body: (
        <>
          <P>
            Washington&apos;s My Health My Data Act, and similar laws in Nevada and
            Connecticut, use a broad definition of &quot;consumer health
            data&quot; that includes biometric data. On that basis, the facial
            reference image and comparison scores described above are the consumer
            health data we process. We collect them from you, through the
            verification flow, for the purposes in section 3, and we share them
            only with the two processors named in section 5.
          </P>
          <P>We want to be equally clear about what we do <em>not</em> do:</P>
          <Ul
            items={[
              'We do not ask you for any health information, and Tribys has no field for it.',
              'We do not infer your health status, physical or mental, from anything you do in the app.',
              'We do not analyse your interests, the groups you join, or the places you go to derive health, condition, treatment, reproductive, sexual-health or gender-affirming-care information about you — even though a group or an interest you choose might touch on such a subject.',
              'We do not use the facial reference image to estimate or infer anything about your body or health. It is used only to check whether two photos show the same person. Our verification processor does produce an age estimate during its check; we neither store nor use it.',
              'We do not sell consumer health data, and we would need your separate written authorisation to do so — which we will never ask for.',
            ]}
          />
        </>
      ),
    },
    {
      id: 'rights',
      heading: '10. Your rights',
      body: (
        <>
          <P>Whatever state you live in, you may:</P>
          <Ul
            items={[
              'ask us to confirm whether we hold biometric or consumer health data about you, and get a copy, including the list of everyone we shared it with;',
              'withdraw your consent, as described in section 4;',
              'have it deleted — we will delete it, and we will pass your deletion request on to the processors in section 5;',
              'complain to us, and to your state attorney general.',
            ]}
          />
          <P>
            Email <Tok value={entity.privacyEmail} />. We do not charge for this,
            we will not treat you differently for asking, and we respond within 45
            days. Deleting your account through Settings achieves the same
            deletion, immediately and without needing to ask.
          </P>
          <P>
            Illinois residents: BIPA gives you a private right of action, and
            nothing in this notice or in our Terms limits it.
          </P>
        </>
      ),
    },
    {
      id: 'contact',
      heading: '11. Contact',
      body: (
        <P>
          <Tok value={entity.legalName} />
          <br />
          <Tok value={entity.address} />
          <br />
          <Tok value={entity.privacyEmail} />
        </P>
      ),
    },
  ],
};
